// ── Conectar WhatsApp (2026-08-19) ───────────────────────────────────────────
// Escrito para alguien que nunca oyó "API key". El cliente se registra en
// 360dialog, paga él y es dueño de su número; nosotros solo guardamos la clave.
//
// Las instrucciones van ANTES del campo, no como ayuda al costado: quien llega
// acá sin haber hecho el alta no tiene nada que pegar todavía.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Field } from '../ui/Field.js';
import { Pill } from '../PillGroup.js';
import { Icon } from '../Icon.js';
import { useToast } from '../ui/Toast.js';
import { ApiError } from '../../lib/api.js';
import {
  useWhatsappConnection, useConnectWhatsapp, useDisconnectWhatsapp, useMarkAppOpened,
} from '../../hooks/useWhatsapp.js';

function Paso({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li style={{ display: 'flex', gap: 'var(--evo-space-3)', alignItems: 'flex-start' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '20px', height: '20px', borderRadius: 'var(--evo-r-pill)', flexShrink: 0, marginTop: '1px',
        fontSize: 'var(--evo-fs-caption)', background: 'var(--evo-bg-2)',
        color: 'var(--evo-fg-3)', border: '1px solid var(--evo-stroke)',
      }}>{n}</span>
      <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', lineHeight: 1.6 }}>{children}</span>
    </li>
  );
}

export function WhatsappConnect() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data } = useWhatsappConnection();
  const connect = useConnectWhatsapp();
  const disconnect = useDisconnectWhatsapp();
  const markOpened = useMarkAppOpened();
  const [key, setKey] = useState('');

  async function guardar() {
    try {
      const r = await connect.mutateAsync(key.trim());
      setKey('');
      toast.success(r.phoneNumber
        ? t('whatsapp.connectedToast', { number: r.phoneNumber })
        : t('whatsapp.connected'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  const conectado = data?.connected === true;
  const dias = data?.daysSinceAppActivity ?? null;
  const porVencer = conectado && dias != null && data != null && dias >= data.warnDays;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
        <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', flex: 1, minWidth: 0 }}>
          WhatsApp
        </span>
        <Pill size="sm" variant="subtle" tone={conectado ? 'success' : 'muted'}>
          {conectado ? t('whatsapp.connected') : t('whatsapp.notConnected')}
        </Pill>
      </div>

      {conectado && (
        <>
          <Field label={t('whatsapp.numberLabel')}>
            {/* 360dialog no siempre dice el número al conectar. El canal
                funciona igual —el envío va con la clave— y el número aparece
                solo con el primer mensaje que entra. */}
            <span style={{
              fontSize: 'var(--evo-fs-body)',
              color: data?.phoneNumber ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)',
            }}>
              {data?.phoneNumber ?? t('whatsapp.numberPending')}
            </span>
          </Field>

          {/* Coexistence: si no abre la app cada 13 días, Meta desactiva la
              cuenta y deja de llegar todo. Se avisa ANTES, no cuando ya pasó. */}
          {porVencer && data && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)',
              padding: 'var(--evo-space-3) var(--evo-space-4)',
              background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r)',
              border: '1px solid var(--evo-stroke)', borderLeft: '2px solid var(--evo-warning)',
            }}>
              <span style={{ color: 'var(--evo-warning)', display: 'inline-flex', flexShrink: 0 }}>
                <Icon name="alert" size={16} />
              </span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warning)', fontWeight: 'var(--evo-fw-semibold)' }}>
                  {t('whatsapp.appReminderTitle', { days: Math.max(data.limitDays - (dias ?? 0), 0) })}
                </span>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
                  {t('whatsapp.appReminderBody')}
                </span>
                <div>
                  <Button variant="outline" tone="cyan" size="sm" loading={markOpened.isPending}
                    onClick={() => { markOpened.mutate(); }}>
                    {t('whatsapp.appOpenedBtn')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div>
            <Button variant="outline" tone="danger" size="sm" loading={disconnect.isPending}
              onClick={() => { disconnect.mutate(); }}>
              {t('whatsapp.disconnectBtn')}
            </Button>
          </div>
        </>
      )}

      {!conectado && (
        <>
          <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
            {t('whatsapp.intro')}
          </p>

          <ol style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)', listStyle: 'none', padding: 0, margin: 0 }}>
            <Paso n={1}>{t('whatsapp.step1')}</Paso>
            <Paso n={2}>{t('whatsapp.step2')}</Paso>
            <Paso n={3}>{t('whatsapp.step3')}</Paso>
            <Paso n={4}>{t('whatsapp.step4')}</Paso>
          </ol>

          {/* La advertencia del número, ANTES del campo. Sorprende a todo el
              mundo y con la migración clásica no se vuelve atrás sin trámite. */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)',
            padding: 'var(--evo-space-3) var(--evo-space-4)',
            background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r)',
            border: '1px solid var(--evo-stroke)', borderLeft: '2px solid var(--evo-warning)',
          }}>
            <span style={{ color: 'var(--evo-warning)', display: 'inline-flex', flexShrink: 0 }}>
              <Icon name="alert" size={16} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warning)', fontWeight: 'var(--evo-fw-semibold)' }}>
                {t('whatsapp.warningTitle')}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
                {t('whatsapp.warningBody')}
              </span>
            </div>
          </div>

          <Field label={t('whatsapp.keyLabel')} hint={t('whatsapp.keyHint')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
              <Input value={key} onChange={setKey} placeholder="••••••••••••••••" fullWidth />
              <Button variant="solid" tone="cyan" size="sm" loading={connect.isPending}
                disabled={!key.trim()} onClick={() => { void guardar(); }}>
                {t('whatsapp.connectBtn')}
              </Button>
            </div>
          </Field>
        </>
      )}
    </div>
  );
}
